import React from 'react';
import { Star, MoreVertical } from 'lucide-react';
import { BrandActionsMenu } from './BrandActionsMenu';
import { BrandStatusPopup } from './BrandStatusPopup';

interface BrandGridCardProps {
  brand: any;
  isGlobalDarkMode: boolean;
  bookmarkedBrands: Set<string>;
  handleBookmarkToggle: (brandId: string, e: React.MouseEvent) => void;
  handleView: (brand: any) => void;
  handlePrefetch: (brandId: string) => void;
  getStatusInfo: (status: string) => any;
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
  handleDelete: (id: string) => void;
  handleAssignTeamMember: (id: string) => void;
  showStatusPopup: string | null;
  setShowStatusPopup: (id: string | null) => void;
  handleStatusChange: (brandId: string, status: 'live' | 'private' | 'draft') => void;
}

export const BrandGridCard: React.FC<BrandGridCardProps> = ({
  brand,
  isGlobalDarkMode,
  bookmarkedBrands,
  handleBookmarkToggle,
  handleView,
  handlePrefetch,
  getStatusInfo,
  openMenuId,
  setOpenMenuId,
  handleDelete,
  handleAssignTeamMember,
  showStatusPopup,
  setShowStatusPopup,
  handleStatusChange
}) => {
  const isBookmarked = bookmarkedBrands.has(brand.id);

  return (
    <div
      key={brand.id}
      className="group rounded-2xl bg-card text-card-foreground border border-border shadow-sm transition-all duration-200 hover:shadow-md overflow-hidden cursor-pointer"
      onMouseEnter={() => handlePrefetch(brand.id)}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.status-container') ||
          target.closest('button') ||
          showStatusPopup === brand.id ||
          openMenuId === brand.id) {
          return;
        }
        handleView(brand);
      }}
    >
      {/* Cover image area */}
      <div
        className="h-52 flex items-center justify-center relative overflow-hidden rounded-b-lg"
        style={{
          backgroundColor: brand.sections[0]?.styles?.backgroundColor || '#3B82F6'
        }}
      >
        {brand.brandData?.brandThumbnail ? (
          <img
            src={brand.brandData.brandThumbnail}
            alt={brand.name}
            className="w-full h-full object-cover transition-transform duration-300"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.parentElement?.querySelector('.fallback-text');
              if (fallback) {
                (fallback as HTMLElement).style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div
          className={`text-3xl font-bold text-white absolute inset-0 flex items-center justify-center transition-transform duration-300 ${brand.brandData?.brandThumbnail ? 'fallback-text hidden' : ''
            }`}
        >
          {brand.name}
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />

        <div className="status-container">
          <BrandStatusPopup
            brand={brand}
            isGlobalDarkMode={isGlobalDarkMode}
            showStatusPopup={showStatusPopup}
            setShowStatusPopup={setShowStatusPopup}
            handleStatusChange={handleStatusChange}
            getStatusInfo={getStatusInfo}
            isGridView={true}
          />
        </div>

        <div className="absolute top-4 right-4">
          <button
            onClick={(e) => handleBookmarkToggle(brand.id, e)}
            className={`p-1 rounded-full transition-all duration-200 ${isBookmarked
                ? 'text-yellow-500'
                : 'text-white/70 hover:text-yellow-400'
              }`}
            title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            <Star className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />
          </button>
        </div>
      </div>

      {/* Card body */}
      <div className="py-5 px-6 relative">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-lg font-semibold leading-none truncate text-foreground">{brand.name}</div>
            <div className="mt-2 text-sm text-muted-foreground">
              {brand.sections?.length || 0} sections • {brand.brandData?.industry || 'No industry'}
              {' • '}
              {new Date(brand.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </div>
          </div>

          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(openMenuId === brand.id ? null : brand.id);
              }}
              className="p-2 rounded-full transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
              aria-label="Brand actions"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {openMenuId === brand.id && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(null);
                  }}
                />
                <div className="absolute bottom-10 right-0 z-40 rounded-xl border border-border bg-popover shadow-lg min-w-[170px] p-1.5">
                  <BrandActionsMenu
                    brandId={brand.id}
                    isGlobalDarkMode={isGlobalDarkMode}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                    handleDelete={handleDelete}
                    handleAssignTeamMember={handleAssignTeamMember}
                    handleArchive={(id) => {
                      console.log('Archive brand:', id);
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
